import { useState } from 'react';
import { api, type Attestation } from '../api.ts';
import { asDate, message } from './format.ts';

const ATTESTATION_PROMPTS: Record<string, string> = {
  'AC-005.breakGlassAccounts':
    'Object ids of the designated emergency access accounts, comma separated.',
  'AC-005.documented':
    'Is the break-glass procedure and credential custody written down and current?',
  'AC-013.adminOnlyAccounts':
    'Object ids of accounts intended for administration only, comma separated.',
};

interface RowProps {
  attestationKey: string;
  existing: Attestation | undefined;
  tenantId: string;
  onSaved: () => void;
  onError: (message: string) => void;
}

export const AttestationRow = ({
  attestationKey,
  existing,
  tenantId,
  onSaved,
  onError,
}: RowProps) => {
  const [answer, setAnswer] = useState<Attestation['answer']>(existing?.answer ?? 'unknown');
  const [values, setValues] = useState((existing?.values ?? []).join(', '));
  const [note, setNote] = useState(existing?.note ?? '');
  const [attestedBy, setAttestedBy] = useState(existing?.attestedBy ?? '');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const field = (name: string): string => `${attestationKey}-${name}`;

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await api.putAttestation(tenantId, {
        key: attestationKey,
        answer,
        values: values
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean),
        note: note.trim() || null,
        attestedBy: attestedBy.trim(),
      });
      setStatus('Saved. Any control resting on this answer is marked as attested.');
      onSaved();
    } catch (cause) {
      onError(message(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="mono">{attestationKey}</h2>
        <span className="attested" />
      </div>
      <div className="card-body">
        <p className="note td-prompt">{ATTESTATION_PROMPTS[attestationKey] ?? ''}</p>

        <div className="td-fields">
          <div className="field">
            <label htmlFor={field('answer')}>Answer</label>
            <select
              id={field('answer')}
              value={answer}
              onChange={(event) => setAnswer(event.target.value as Attestation['answer'])}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="unknown">Not yet known</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor={field('values')}>Accounts (object ids)</label>
            <input
              id={field('values')}
              value={values}
              onChange={(event) => setValues(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor={field('by')}>Attested by</label>
            <input
              id={field('by')}
              value={attestedBy}
              placeholder="name@msp.example"
              onChange={(event) => setAttestedBy(event.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor={field('note')}>Note</label>
          <input
            id={field('note')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        <div className="toolbar">
          <button
            type="button"
            className="btn btn-primary"
            aria-busy={saving}
            disabled={saving || !attestedBy.trim()}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save attestation'}
          </button>
          <span className="status-inline" role="status">
            {status}
          </span>
          {existing && status === null && (
            <span className="note">
              Last answered {asDate(existing.attestedAt)} by {existing.attestedBy}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
